import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import type { Theme } from './themes';

// ID fixo da linha de configuração no banco (UUID real)
let _configRowId: string | null = null;

/**
 * Garante que existe uma linha de configuração e retorna seu ID
 */
const ensureConfigRow = async (): Promise<string> => {
  if (_configRowId) return _configRowId;

  const { data, error } = await supabase
    .from('ibx_custom_themes')
    .select('id')
    .maybeSingle();

  if (error) throw error;

  if (data?.id) {
    _configRowId = data.id;
    return data.id;
  }

  // Cria a linha se não existir
  const { data: inserted, error: insertError } = await supabase
    .from('ibx_custom_themes')
    .insert({ active_theme_id: 'padrao' })
    .select('id')
    .single();

  if (insertError) throw insertError;
  _configRowId = inserted.id;
  return inserted.id;
};

/**
 * Converte cor hex para componentes RGB separados por vírgula
 */
const hexToRgb = (hex: string): string => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '20, 184, 166';
  return `${r}, ${g}, ${b}`;
};

/**
 * Aplica as variáveis CSS do tema no documento
 */
export const applyThemeVariables = (theme: Theme): void => {
  if (!theme) return;
  const root = document.documentElement;

  // Cores principais
  const colors = theme.colors || ({} as any);
  root.style.setProperty('--color-primary', colors.primary || '#14B8A6');
  root.style.setProperty('--color-secondary', colors.secondary || '#0F172A');
  root.style.setProperty('--color-accent', colors.accent || '#F59E0B');
  root.style.setProperty('--color-background', colors.background || '#FFFFFF');
  root.style.setProperty('--color-surface', colors.surface || '#F8FAFC');
  root.style.setProperty('--color-border', colors.border || '#E2E8F0');

  // Texto — suporta tanto string plana quanto objeto { primary, secondary }
  const textColor = typeof colors.text === 'string'
    ? colors.text
    : (colors.text?.primary || '#0F172A');
  const mutedTextColor = typeof colors.text === 'object'
    ? (colors.text?.secondary || '#64748B')
    : (colors.mutedText || '#64748B');
  root.style.setProperty('--color-text', textColor);
  root.style.setProperty('--color-muted-text', mutedTextColor);

  // Cores de estado
  root.style.setProperty('--color-success', colors.success || '#10B981');
  root.style.setProperty('--color-warning', colors.warning || '#F59E0B');
  root.style.setProperty('--color-error', colors.error || '#EF4444');
  root.style.setProperty('--color-info', colors.info || '#3B82F6');

  // CTA (opcional)
  if (colors.cta) {
    root.style.setProperty('--color-cta', colors.cta);
  }

  // Variáveis RGB para uso com rgba()
  root.style.setProperty('--color-primary-rgb', hexToRgb(colors.primary || '#14B8A6'));
  root.style.setProperty('--color-secondary-rgb', hexToRgb(colors.secondary || '#0F172A'));
  root.style.setProperty('--color-accent-rgb', hexToRgb(colors.accent || '#F59E0B'));

  // Tipografia — suporta tanto estrutura plana quanto aninhada
  const typo = theme.typography || ({} as any);
  const headingFont = typo.headingFont || typo.fontFamily?.heading || "'Inter', sans-serif";
  const bodyFont = typo.bodyFont || typo.fontFamily?.body || "'Inter', sans-serif";
  const baseSize = typo.baseSize || typo.fontSize?.base || '16px';
  const headingWeight = typo.headingWeight || '700';
  const bodyWeight = typo.bodyWeight || '400';
  root.style.setProperty('--font-heading', headingFont);
  root.style.setProperty('--font-body', bodyFont);
  root.style.setProperty('--font-size-base', baseSize);
  root.style.setProperty('--font-weight-heading', headingWeight);
  root.style.setProperty('--font-weight-body', bodyWeight);

  // Raios — suporta tanto theme.radius quanto theme.radius
  const radius = theme.radius || ({} as any);
  root.style.setProperty('--radius-sm', radius.sm || '0.375rem');
  root.style.setProperty('--radius-md', radius.md || '0.5rem');
  root.style.setProperty('--radius-lg', radius.lg || '0.75rem');
  root.style.setProperty('--radius-xl', radius.xl || '1rem');
  root.style.setProperty('--radius-full', radius.full || '9999px');

  // Sombras — suporta tanto theme.shadow (plano) quanto theme.shadows (aninhado)
  const shadow = (theme as any).shadow || ({} as any);
  const shadows = (theme as any).shadows || ({} as any);
  root.style.setProperty('--shadow-card', shadow.card || shadows.md || '0 4px 6px -1px rgba(0,0,0,0.1)');
  root.style.setProperty('--shadow-button', shadow.button || shadows.sm || '0 1px 2px 0 rgba(0,0,0,0.05)');
  root.style.setProperty('--shadow-input', shadow.input || shadows.sm || '0 1px 2px 0 rgba(0,0,0,0.05)');
  root.style.setProperty('--shadow-dropdown', shadow.dropdown || shadows.lg || '0 10px 15px -3px rgba(0,0,0,0.1)');
  root.style.setProperty('--shadow-modal', shadow.modal || shadows.xl || '0 20px 25px -5px rgba(0,0,0,0.1)');

  // Espaçamento
  const spacing = theme.spacing || ({} as any);
  root.style.setProperty('--spacing-xs', spacing.xs || '0.5rem');
  root.style.setProperty('--spacing-sm', spacing.sm || '1rem');
  root.style.setProperty('--spacing-md', spacing.md || '1.5rem');
  root.style.setProperty('--spacing-lg', spacing.lg || '2rem');
  root.style.setProperty('--spacing-xl', spacing.xl || '3rem');

  // Botões — suporta tanto estrutura plana quanto aninhada
  const buttons = (theme as any).buttons || ({} as any);
  root.style.setProperty('--button-text-transform', buttons.textTransform || 'none');
  root.style.setProperty('--button-font-weight', buttons.fontWeight || '600');
  root.style.setProperty('--button-padding-x', buttons.paddingX || '24px');
  root.style.setProperty('--button-padding-y', buttons.paddingY || '12px');

  // Cores de botões (estrutura aninhada do defaultThemes)
  if (buttons.primary?.background) {
    root.style.setProperty('--button-primary-bg', buttons.primary.background);
    root.style.setProperty('--button-primary-text', buttons.primary.text || '#FFFFFF');
    root.style.setProperty('--button-primary-hover', buttons.primary.hover || buttons.primary.background);
  }
  if (buttons.accent?.background) {
    root.style.setProperty('--button-cta-bg', buttons.accent.background);
    root.style.setProperty('--button-cta-text', buttons.accent.text || '#FFFFFF');
    root.style.setProperty('--button-cta-hover', buttons.accent.hover || buttons.accent.background);
  }
  if (buttons.cta?.background) {
    root.style.setProperty('--button-cta-bg', buttons.cta.background);
    root.style.setProperty('--button-cta-text', buttons.cta.text || '#FFFFFF');
    root.style.setProperty('--button-cta-hover', buttons.cta.hover || buttons.cta.background);
  }

  // Cards
  const cards = (theme as any).cards || ({} as any);
  root.style.setProperty('--card-background', cards.background || '#FFFFFF');
  root.style.setProperty('--card-border', cards.border || '#E2E8F0');
  root.style.setProperty('--card-shadow', cards.shadow || shadows.md || '0 4px 6px -1px rgba(0,0,0,0.1)');
  root.style.setProperty('--card-padding', cards.padding || '24px');

  // Inputs
  const inputs = (theme as any).inputs || ({} as any);
  root.style.setProperty('--input-background', inputs.background || '#FFFFFF');
  root.style.setProperty('--input-border', inputs.border || '#E2E8F0');
  root.style.setProperty('--input-focus-border', inputs.focusBorder || inputs.focus || colors.primary || '#14B8A6');
  root.style.setProperty('--input-padding', inputs.padding || '12px 16px');
  root.style.setProperty('--input-height', inputs.height || '48px');

  // Gradientes (opcional — apenas tema corporativo IBX)
  const gradients = (theme as any).gradients || ({} as any);
  if (gradients.hero) {
    root.style.setProperty('--gradient-hero', gradients.hero);
  }
  if (gradients.card) {
    root.style.setProperty('--gradient-card', gradients.card);
  }
  if (gradients.overlay) {
    root.style.setProperty('--gradient-overlay', gradients.overlay);
  }
};

/**
 * Salva o tema ativo no banco
 */
export const saveActiveTheme = async (themeId: string): Promise<void> => {
  try {
    const rowId = await ensureConfigRow();
    const { error } = await supabase
      .from('ibx_custom_themes')
      .update({ active_theme_id: themeId, updated_at: new Date().toISOString() })
      .eq('id', rowId);
    if (error) throw error;
  } catch (error) {
    logger.error('Erro ao salvar tema ativo:', error);
  }
};

/**
 * Recupera o ID do tema ativo
 */
export const getActiveTheme = async (): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('ibx_custom_themes')
      .select('id, active_theme_id')
      .maybeSingle();

    if (error) throw error;
    if (data?.id) _configRowId = data.id;
    return data?.active_theme_id || null;
  } catch (error) {
    logger.error('Erro ao recuperar tema ativo:', error);
    return null;
  }
};

/**
 * Salva temas personalizados no banco COM snapshot original
 * Implementa sistema de versionamento: themes (atual) + themes_original (imutável)
 */
export const saveCustomThemes = async (themes: Theme[]): Promise<void> => {
  try {
    const rowId = await ensureConfigRow();
    
    // Busca os temas originais existentes
    const { data: existing } = await supabase
      .from('ibx_custom_themes')
      .select('themes_original')
      .eq('id', rowId)
      .maybeSingle();

    const existingOriginals = (existing?.themes_original as Theme[]) || [];
    
    // Para cada tema sendo salvo, preserva ou cria o snapshot original
    const updatedOriginals = themes.map(theme => {
      // Busca se já existe um original para este tema
      const existingOriginal = existingOriginals.find(orig => orig.id === theme.id);
      
      // Se já existe original, mantém ele (IMUTÁVEL)
      // Se não existe, cria snapshot do estado atual como original
      return existingOriginal || { ...theme };
    });

    // Salva tanto o tema atual quanto o original
    const { error } = await supabase
      .from('ibx_custom_themes')
      .update({ 
        themes,
        themes_original: updatedOriginals,
        updated_at: new Date().toISOString() 
      })
      .eq('id', rowId);
      
    if (error) throw error;
    
    logger.info('Temas salvos com snapshot original preservado', { 
      themesCount: themes.length,
      originalsCount: updatedOriginals.length 
    });
  } catch (error) {
    logger.error('Erro ao salvar temas personalizados:', error);
  }
};

/**
 * Recupera temas personalizados do banco (versão atual)
 */
export const getCustomThemes = async (): Promise<Theme[]> => {
  try {
    const { data, error } = await supabase
      .from('ibx_custom_themes')
      .select('id, themes')
      .maybeSingle();

    if (error) throw error;
    if (data?.id) _configRowId = data.id;
    return (data?.themes as Theme[]) || [];
  } catch (error) {
    logger.error('Erro ao recuperar temas personalizados:', error);
    return [];
  }
};

/**
 * 🆕 Recupera o snapshot ORIGINAL de um tema específico (imutável)
 * Usado pelo botão "Restaurar original"
 */
export const getOriginalTheme = async (themeId: string): Promise<Theme | null> => {
  try {
    const rowId = await ensureConfigRow();
    const { data, error } = await supabase
      .from('ibx_custom_themes')
      .select('themes_original')
      .eq('id', rowId)
      .maybeSingle();

    if (error) throw error;
    
    const originals = (data?.themes_original as Theme[]) || [];
    const original = originals.find(t => t.id === themeId);
    
    if (original) {
      logger.info('Snapshot original recuperado', { themeId, themeName: original.name });
    }
    
    return original || null;
  } catch (error) {
    logger.error('Erro ao recuperar tema original:', error);
    return null;
  }
};

/**
 * 🆕 Recupera TODOS os snapshots originais
 * Usado pelo ThemeContext para manter referência imutável
 */
export const getAllOriginalThemes = async (): Promise<Theme[]> => {
  try {
    const rowId = await ensureConfigRow();
    const { data, error } = await supabase
      .from('ibx_custom_themes')
      .select('themes, themes_original')
      .eq('id', rowId)
      .maybeSingle();

    if (error) throw error;

    const originals = (data?.themes_original as Theme[]) || [];
    const currents = (data?.themes as Theme[]) || [];

    // Se themes_original está vazio mas há temas salvos,
    // usa os temas atuais como originais e persiste no banco
    if (originals.length === 0 && currents.length > 0) {
      logger.warn('themes_original vazio — recriando snapshots a partir dos temas atuais', {
        themesCount: currents.length,
      });

      const { error: updateError } = await supabase
        .from('ibx_custom_themes')
        .update({ themes_original: currents, updated_at: new Date().toISOString() })
        .eq('id', rowId);

      if (!updateError) {
        logger.info('Snapshots originais recriados com sucesso', { count: currents.length });
        return currents;
      }
    }

    return originals;
  } catch (error) {
    logger.error('Erro ao recuperar temas originais:', error);
    return [];
  }
};

/**
 * Gera um ID único para novos temas
 */
export const generateThemeId = (): string => {
  return `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Duplica um tema existente
 */
export const duplicateTheme = (theme: Theme): Theme => {
  return {
    ...theme,
    id: generateThemeId(),
    name: `${theme.name} (Cópia)`,
    active: false,
    isDefault: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

/**
 * Valida se um tema possui todas as propriedades necessárias
 */
export const validateTheme = (theme: Partial<Theme>): boolean => {
  const requiredFields = ['id', 'name', 'colors', 'typography', 'radius', 'shadow', 'spacing', 'buttons', 'cards', 'inputs'];
  return requiredFields.every((field) => field in theme);
};

/**
 * Cria um tema vazio para edição
 */
export const createEmptyTheme = (): Theme => {
  return {
    id: generateThemeId(),
    name: 'Novo Tema',
    description: 'Tema personalizado',
    active: false,
    isDefault: false,
    colors: {
      primary: '#14B8A6',
      secondary: '#0F172A',
      accent: '#F59E0B',
      background: '#FFFFFF',
      surface: '#F8FAFC',
      text: '#0F172A',
      mutedText: '#64748B',
      border: '#E2E8F0',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
    },
    typography: {
      headingFont: "'Inter', sans-serif",
      bodyFont: "'Inter', sans-serif",
      baseSize: '16px',
      headingWeight: '700',
      bodyWeight: '400',
    },
    radius: { sm: '8px', md: '12px', lg: '16px', xl: '20px', full: '9999px' },
    shadow: {
      card: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      button: '0 4px 12px rgba(20, 184, 166, 0.3)',
      input: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      dropdown: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      modal: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
    buttons: { variant: 'solid', textTransform: 'none', fontWeight: '600', paddingX: '24px', paddingY: '12px' },
    cards: { background: '#FFFFFF', border: '#E2E8F0', shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', padding: '24px' },
    inputs: { background: '#FFFFFF', border: '#E2E8F0', focusBorder: '#14B8A6', padding: '12px 16px', height: '48px' },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

/**
 * Exporta um tema como JSON
 */
export const exportTheme = (theme: Theme): string => {
  return JSON.stringify(theme, null, 2);
};

/**
 * Importa um tema de JSON
 */
export const importTheme = (jsonString: string): Theme => {
  try {
    const theme = JSON.parse(jsonString) as Theme;
    if (!theme.id || !theme.name || !theme.colors) {
      throw new Error('Formato de tema inválido');
    }
    return theme;
  } catch (error) {
    logger.error('Erro ao importar tema:', error);
    throw new Error('Arquivo de tema inválido');
  }
};
